/**
 * parseVmsk — decode PSD vector mask path data (vmsk / vsms keys).
 *
 * PSD format (vmsk / vsms additional-layer-info blocks):
 *   Bytes 0-3:  version (big-endian uint32; always 3 in practice)
 *   Bytes 4-7:  flags (bit 0 = invert, bit 1 = not-linked; usually 0)
 *   Bytes 8…:   N × 26-byte path records (see below)
 *   (optional trailing 2-byte alignment pad)
 *
 * Each 26-byte path record:
 *   Bytes 0-1:  selector (uint16, big-endian)
 *     0 = Closed subpath length record
 *     1 = Closed subpath Bezier knot, linked
 *     2 = Closed subpath Bezier knot, unlinked
 *     3 = Open subpath length record
 *     4 = Open subpath Bezier knot, linked
 *     5 = Open subpath Bezier knot, unlinked
 *     6 = Path fill rule record (ignored)
 *     7 = Clipboard record (ignored)
 *     8 = Initial fill rule record (ignored)
 *   For length records (sel 0/3):
 *     Bytes 2-3: knot count (uint16, big-endian)
 *     Bytes 4-25: unused padding
 *   For Bezier knot records (sel 1/2/4/5):
 *     Bytes 2-5:  preceding control point — vertical (signed 8.24 fixed-point)
 *     Bytes 6-9:  preceding control point — horizontal (signed 8.24 fixed-point)
 *     Bytes 10-13: anchor point — vertical
 *     Bytes 14-17: anchor point — horizontal
 *     Bytes 18-21: leaving control point — vertical
 *     Bytes 22-25: leaving control point — horizontal
 *
 * The 8.24 fixed-point values are fractions of the document dimension
 * (vertical values are fractions of height, horizontal of width).  Multiply
 * by docWidth / docHeight to obtain document-space pixel coordinates.
 *
 * Reference: Adobe Photoshop File Formats Specification, "Path Resource Format"
 * and "Additional Layer Information" (keys vmsk, vsms).
 */

import type { PathModel, SubPath, AnchorPoint } from "../../document/PathModel";

// PSD path record selectors
const SEL_CLOSED_LEN = 0;
const SEL_CLOSED_LINKED = 1;
const SEL_CLOSED_UNLINKED = 2;
const SEL_OPEN_LEN = 3;
const SEL_OPEN_LINKED = 4;
const SEL_OPEN_UNLINKED = 5;

const HEADER_SIZE = 8; // version (4) + flags (4)
const RECORD_SIZE = 26;

/**
 * Read a signed 8.24 fixed-point value from 4 big-endian bytes.
 * Returns the value as a plain floating-point fraction.
 */
function readFixed8_24(data: Uint8Array, offset: number): number {
  const view = new DataView(data.buffer, data.byteOffset + offset, 4);
  // Signed big-endian int32, then divide by 2^24 to get the fractional value.
  return view.getInt32(0, false) / 16777216; // 2^24
}

/**
 * Parse a vmsk or vsms additional-layer-info payload into a {@link PathModel}.
 *
 * @param data   Raw payload bytes from the additional-info block.
 * @param docWidth   Document pixel width (used to convert horizontal fractions).
 * @param docHeight  Document pixel height (used to convert vertical fractions).
 * @returns A PathModel with one or more subpaths, or null if the data is too
 *   short or contains no usable path records.
 */
export function parseVmsk(
  data: Uint8Array,
  docWidth: number,
  docHeight: number,
): PathModel | null {
  if (data.length < HEADER_SIZE + RECORD_SIZE) return null;

  const numRecords = Math.floor((data.length - HEADER_SIZE) / RECORD_SIZE);
  if (numRecords === 0) return null;

  const subpaths: SubPath[] = [];
  let i = 0;

  while (i < numRecords) {
    const off = HEADER_SIZE + i * RECORD_SIZE;
    const sel = (data[off]! << 8) | data[off + 1]!;

    if (sel === SEL_CLOSED_LEN || sel === SEL_OPEN_LEN) {
      const closed = sel === SEL_CLOSED_LEN;
      const knotCount = (data[off + 2]! << 8) | data[off + 3]!;
      i++;

      const points: AnchorPoint[] = [];
      let k = 0;
      while (k < knotCount && i < numRecords) {
        const koff = HEADER_SIZE + i * RECORD_SIZE;
        const ksel = (data[koff]! << 8) | data[koff + 1]!;

        if (
          ksel !== SEL_CLOSED_LINKED &&
          ksel !== SEL_CLOSED_UNLINKED &&
          ksel !== SEL_OPEN_LINKED &&
          ksel !== SEL_OPEN_UNLINKED
        ) {
          // Unexpected record type inside knot sequence — stop this subpath.
          break;
        }

        // Preceding control point
        const prevV = readFixed8_24(data, koff + 2);
        const prevH = readFixed8_24(data, koff + 6);
        // Anchor point
        const ancV = readFixed8_24(data, koff + 10);
        const ancH = readFixed8_24(data, koff + 14);
        // Leaving control point
        const leaveV = readFixed8_24(data, koff + 18);
        const leaveH = readFixed8_24(data, koff + 22);

        // Convert fractions to document-space pixels.
        // PSD stores: vertical component = fraction of docHeight,
        //             horizontal component = fraction of docWidth.
        const x = ancH * docWidth;
        const y = ancV * docHeight;
        const leftX = prevH * docWidth;
        const leftY = prevV * docHeight;
        const rightX = leaveH * docWidth;
        const rightY = leaveV * docHeight;

        // Determine point type: if control points match the anchor, it's a corner.
        const isCorner =
          Math.abs(leftX - x) < 0.5 &&
          Math.abs(leftY - y) < 0.5 &&
          Math.abs(rightX - x) < 0.5 &&
          Math.abs(rightY - y) < 0.5;

        const linked = ksel === SEL_CLOSED_LINKED || ksel === SEL_OPEN_LINKED;
        const pointType = isCorner ? "corner" : linked ? "smooth" : "corner";

        const point: AnchorPoint = { x, y, type: pointType };
        if (!isCorner) {
          point.leftDirection = { x: leftX, y: leftY };
          point.rightDirection = { x: rightX, y: rightY };
        }
        points.push(point);
        i++;
        k++;
      }

      if (points.length > 0) {
        subpaths.push({ points, closed });
      }
    } else {
      // Skip fill rule, clipboard, initial fill, or unknown records.
      i++;
    }
  }

  if (subpaths.length === 0) return null;
  return { subpaths };
}

/**
 * Parse PSD image resource 1032 (grid and guides) and return a list of guide
 * objects compatible with the app's Guide interface.
 *
 * Format (from the Adobe PSD spec, "Image Resources Section"):
 *   Bytes 0-3:  future — horizontal grid spacing in 1/32 pixels (ignored)
 *   Bytes 4-7:  future — vertical grid spacing in 1/32 pixels (ignored)
 *   Bytes 8-9:  guide count (uint16, big-endian)
 *   Per guide (5 bytes):
 *     Bytes 0-3: position in 1/32 pixels (signed int32, big-endian)
 *     Byte  4:   direction — 0 = vertical guide (at X position),
 *                            1 = horizontal guide (at Y position)
 *
 * Returns an array of { kind, position } guide objects.
 */
export function parseGuideResource(
  data: Uint8Array,
): Array<{ kind: "horizontal" | "vertical"; position: number }> {
  if (data.length < 10) return [];
  const view = new DataView(data.buffer, data.byteOffset, data.length);
  const count = view.getUint16(8, false);
  if (count === 0) return [];

  const guides: Array<{ kind: "horizontal" | "vertical"; position: number }> = [];
  for (let i = 0; i < count; i++) {
    const byteOffset = 10 + i * 5;
    if (byteOffset + 5 > data.length) break;
    const loc = view.getInt32(byteOffset, false);
    const dir = data[byteOffset + 4]!;
    // Position is stored in 1/32 px units — divide by 32 to get doc pixels.
    const position = loc / 32;
    // dir=0 → vertical guide (vertical line at X=position)
    // dir=1 → horizontal guide (horizontal line at Y=position)
    const kind: "horizontal" | "vertical" = dir === 0 ? "vertical" : "horizontal";
    guides.push({ kind, position });
  }
  return guides;
}
